import React, { useEffect, useState } from "react";
import FullScreenPanel from "../../ui/FullScreenPanel.jsx";
import PdfButton from "../../ui/PdfButton.jsx";
import {
  finalizeEmailHeaders, finalizePayload, finalizePreviewState, finalizeProblem, loadFinalizePreview, sendFinalize,
} from "../../../features/salesOrders/finalizeEmail";
import "./guide.css";

// Finalize SO shipment / pickup on a phone: the desktop SO-Finalize dialog's (components/SoFinalizeEmailDialog) reads
// and send, from features/salesOrders/finalizeEmail, in a full-screen sheet. The standard email only, as the Shipping
// hub's operator mode (changing the recipients or the text is an office tool): the order's default recipient, its
// default CCs, the standard subject and message. The PIN is the one the ship / stage just used, else the Working-as
// session's (ui.pin). Layout only.
//
// Props: open, apiBase, plan, kind ('shipping' | 'pickup'), pin ('' = ask), batchManifest ({url, filename, what} |
// null: the batch that just went out), ui, onClose(), onSent(responseBody, {notify}).
export default function FinalizeSheet({ open, apiBase, plan, kind, pin = "", batchManifest = null, ui, onClose, onSent }) {
  const planId = plan ? plan.plan_id : null;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toId, setToId] = useState("");
  const [copySelf, setCopySelf] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || !planId) return undefined;
    let alive = true;
    setLoading(true); setError(""); setPreview(null); setCopySelf(false); setBusy("");
    loadFinalizePreview(planId, apiBase)
      .then((data) => {
        if (!alive) return;
        setPreview(data);
        setToId(data.default_to_contact_id || "");
      })
      .catch((err) => { if (alive) setError(`Could not load email preview: ${err.message || String(err)}`); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, planId, apiBase]);

  const st = finalizePreviewState(preview);
  const isPickup = kind === "pickup";
  const order = (preview && preview.order_number) || (plan && plan.order_number) || "the order";
  const toFixed = !!(preview && preview.default_to_contact_id);
  // never CC the person it goes to (the desktop dialog drops the chosen To from the CCs)
  const ccIds = ((preview && preview.default_cc_contact_ids) || []).filter((id) => String(id) !== String(toId));
  const ccNames = st.contacts.filter((c) => ccIds.includes(c.contact_id)).map((c) => c.name);

  const submit = async (notify) => {
    setError("");
    // the recipient first: no PIN asked for a send that can't go
    const early = finalizeProblem({ pin: pin || "pending", notify, hasAnyRecipientOption: st.hasAnyRecipientOption, selectedToId: toId });
    if (early) { setError(early); return; }
    const who = pin || await ui.pin(`Enter your PIN to finalize ${order} (${isPickup ? "pickup" : "shipment"}).`);
    if (!who) return;
    const problem = finalizeProblem({ pin: who, notify, hasAnyRecipientOption: st.hasAnyRecipientOption, selectedToId: toId });
    if (problem) { setError(problem); return; }
    // the standard text only: no Admin PIN is ever needed here
    const auth = await finalizeEmailHeaders({ notify, edited: false });
    setBusy(notify ? "send" : "skip");
    try {
      const payload = finalizePayload({
        pin: who, notify, selectedToId: toId, customerFallback: st.customerFallback, ccContactIds: ccIds,
        sendCopyToSelf: copySelf, subject: preview ? preview.subject || "" : "", body: preview ? preview.body_text || "" : "",
      });
      const body = await sendFinalize({ planId, kind: isPickup ? "pickup" : "shipping", payload, headers: auth.headers, apiBase });
      onSent(body, { notify });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy("");
    }
  };

  const title = `Finalize ${isPickup ? "pickup" : "shipment"} · ${order}`;
  const footer = preview && !st.finalizeBlocked ? (
    <div className="d-grid gap-2">
      {!st.showNoEmail && (
        <button type="button" className="btn btn-success btn-lg" disabled={!!busy || !st.hasAnyRecipientOption || !toId}
          onClick={() => submit(true)} data-testid="finalize-send">
          {busy === "send" ? "Sending…" : (preview.already_finalized && preview.batch_available
            ? "Send batch manifest & re-finalize" : `Send & finalize ${isPickup ? "pickup" : "shipment"}`)}
        </button>
      )}
      <button type="button" className={`btn btn-lg ${st.showNoEmail ? "btn-success" : "btn-outline-secondary"}`} disabled={!!busy}
        onClick={() => submit(false)} data-testid="finalize-skip">
        {busy === "skip" ? "Finalizing…" : (st.showNoEmail
          ? (preview.already_finalized ? "Re-finalize (no email needed)" : "Finalize (no email needed)")
          : "Finalize without email")}
      </button>
    </div>
  ) : null;

  return (
    <FullScreenPanel open={open} title={title} onClose={busy ? undefined : onClose} footer={footer} testId="phone-guide-finalize">
      <div className="jq-guide">
        {loading && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading the email…</div>}
        {batchManifest && (
          <div className="jq-note jq-note-info">
            <div className="mb-2">This shipment's manifest: the document the customer's email attached.</div>
            <PdfButton url={batchManifest.url} filename={batchManifest.filename} label="This shipment's manifest" testId="finalize-batch-manifest" />
          </div>
        )}
        {preview && st.finalizeBlocked && (
          <div className="jq-note jq-note-warn">
            This order was already finalized{preview.so_finalized_by ? ` by ${preview.so_finalized_by}` : ""}. Finalizing again sends nothing.
          </div>
        )}
        {preview && preview.already_finalized && preview.batch_available && (
          <div className="jq-note jq-note-info">
            {(preview.batch_pallets || []).length} pallet(s) ({(preview.batch_pallets || []).join(", ")}) went out since the last
            finalize: this emails the customer a manifest for just this new batch.
          </div>
        )}
        {st.showNoEmail && (
          <div className="jq-note jq-note-info" data-testid="finalize-no-email">
            Nothing left to email: the customer was already emailed about every pallet{st.notifiedSummary ? ` (${st.notifiedSummary})` : ""}.
            Finalizing records the close-out. No email is sent.
          </div>
        )}
        {preview && !st.finalizeBlocked && st.emailScope === "partial" && (
          <div className="jq-note jq-note-info">
            This email covers only the {st.pendingPallets.length} pallet(s) not emailed yet ({st.pendingPallets.join(", ")}).
          </div>
        )}
        {preview && !st.showNoEmail && !st.finalizeBlocked && (
          <section className="jq-card">
            <label className="form-label fw-semibold" htmlFor="finalize-to">Send to</label>
            <select id="finalize-to" className="form-select" value={toId} disabled={toFixed || !!busy} onChange={(e) => setToId(e.target.value)}>
              {!st.hasAnyRecipientOption && <option value="">No contact with an email on file</option>}
              {st.hasAnyRecipientOption && !toId && <option value="">Pick a recipient</option>}
              {st.contacts.map((c) => <option key={c.contact_id} value={c.contact_id}>{c.name} ({c.email})</option>)}
              {st.customerFallback && (
                <option value="__customer_default__">{st.customerFallback.name || "Customer"} ({st.customerFallback.email})</option>
              )}
            </select>
            {!st.hasAnyRecipientOption && <div className="jq-muted mt-1">Add a contact with an email on the customer record, or finalize without email.</div>}
            {ccNames.length > 0 && <div className="jq-muted mt-2">CC: {ccNames.join(", ")}</div>}
            <div className="form-check mt-2 jq-guide-check">
              <input id="finalize-copy" type="checkbox" className="form-check-input" checked={copySelf} disabled={!!busy}
                onChange={(e) => setCopySelf(e.target.checked)} />
              <label className="form-check-label" htmlFor="finalize-copy">Send a copy to info@kancraftmfg.com</label>
            </div>
            <details className="mt-2">
              <summary className="fw-semibold">The email: {preview.subject}</summary>
              <div className="jq-muted mt-1" style={{ whiteSpace: "pre-line" }}>{preview.body_text}</div>
            </details>
            <div className="jq-muted mt-2">The standard email goes as it is. Changing the recipients or the text is done from a computer.</div>
          </section>
        )}
        {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}
      </div>
    </FullScreenPanel>
  );
}
